<template>
  <div class="corporate-company-form card p-4">
    <h3 class="mb-4">{{ $t("signup.corporate.form_title") }}</h3>

    <form @submit.prevent="handleSubmit">
      <div class="mb-3">
        <label for="name" class="form-label">
          {{ $t("signup.corporate.company_name") }}
          <span class="text-danger">*</span>
        </label>
        <CoreSignalCompanySearchDropdown
          v-model="localCompany.name"
          :countryCode="localCompany.country"
          :placeholder="$t('signup.corporate.company_name')"
          @select="handleCoreSignalCompanySelect"
        />
        <div v-if="v$.name.$error">
          <div
            class="invalid-feedback d-block"
            v-for="error of v$.name.$errors"
            :key="error.$uid"
          >
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div class="mb-3">
        <label for="country" class="form-label">
          {{ $t("signup.corporate.country") }}
          <span class="text-danger">*</span>
        </label>
        <SearchableSelectComponent
          v-model="localCompany.country"
          :options="ALL_COUNTRIES"
          :placeholder="$t('signup.corporate.select_country')"
        />
        <div v-if="v$.country.$error">
          <div
            class="invalid-feedback d-block"
            v-for="error of v$.country.$errors"
            :key="error.$uid"
          >
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div class="mb-3">
        <label for="registry_code" class="form-label">
          {{ $t("signup.corporate.registry_code") }}
          <span class="text-danger">*</span>
        </label>
        <input
          type="text"
          id="registry_code"
          v-model="localCompany.registry_code"
          class="form-control"
          :class="{ 'is-invalid': v$.registry_code.$error }"
          @blur="v$.registry_code.$touch"
        />
        <div
          class="invalid-feedback"
          v-for="error of v$.registry_code.$errors"
          :key="error.$uid"
        >
          {{ error.$message }}
        </div>
      </div>

      <div class="mb-3">
        <label for="address" class="form-label">
          {{ $t("signup.corporate.address") }}
          <span class="text-danger">*</span>
        </label>
        <input
          type="text"
          id="address"
          v-model="localCompany.address"
          class="form-control"
          :class="{ 'is-invalid': v$.address.$error }"
          @blur="v$.address.$touch"
        />
        <div
          class="invalid-feedback"
          v-for="error of v$.address.$errors"
          :key="error.$uid"
        >
          {{ error.$message }}
        </div>
      </div>

      <div class="mb-3">
        <label for="phone_number" class="form-label">
          {{ $t("signup.corporate.phone_number") }}
          <span class="text-danger">*</span>
        </label>
        <PhoneNumberInput
          id="phone_number"
          v-model="localCompany.phone_number"
          :countryCode="localCompany.country"
          :invalid="v$.phone_number.$error"
          @blur="v$.phone_number.$touch"
        />
        <div
          class="invalid-feedback"
          v-for="error of v$.phone_number.$errors"
          :key="error.$uid"
        >
          {{ error.$message }}
        </div>
      </div>

      <div class="mb-3" v-if="isEUCountry">
        <label for="vat" class="form-label">
          {{ $t("signup.corporate.vat") }}
          <span class="text-muted ms-1"
            >({{ $t("signup.corporate.vat_optional") }})</span
          >
        </label>
        <input
          type="text"
          id="vat"
          v-model="localCompany.vat"
          class="form-control"
          :class="{ 'is-invalid': v$.vat.$error }"
          @blur="v$.vat.$touch"
        />
        <div
          class="invalid-feedback"
          v-for="error of v$.vat.$errors"
          :key="error.$uid"
        >
          {{ error.$message }}
        </div>
      </div>

      <div class="d-flex justify-content-end mt-4">
        <button
          type="submit"
          class="btn btn-primary btn-lg"
          :disabled="v$.$invalid || loading"
        >
          <span v-if="loading">
            <span
              class="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
            {{ $t("common.loading") }}
          </span>
          <span v-else>
            {{ $t("signup.corporate.submit") }}
          </span>
        </button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import CoreSignalCompanySearchDropdown from "@/components/common/CoreSignalCompanySearchDropdown.vue";
import PhoneNumberInput from "@/components/common/PhoneNumberInput.vue";
import SearchableSelectComponent from "@/components/common/SearchableSelectComponent.vue";
import {
  fetchCoreSignalCompanyDetails,
  type CoreSignalCompany,
} from "@/composables/coreSignalCompanyApi";
import { useValidators } from "@/composables/useValidators";
import { ALL_COUNTRIES, EU_COUNTRIES } from "@/constants";
import { useVuelidate } from "@vuelidate/core";
import { computed, reactive, watch } from "vue";
import { useI18n } from "vue-i18n";
import type { CorporateCompanyData } from "@/signup/composables/useCorporateSignup";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

const props = defineProps<{
  company: CorporateCompanyData;
  loading?: boolean;
}>();

const emit = defineEmits<{
  submit: [company: CorporateCompanyData];
}>();

const { t } = useI18n();
const validators = useValidators(t);
const { globalErrorHandler } = useGlobalHandlers();

const localCompany = reactive({ ...props.company });

const isEUCountry = computed(() => EU_COUNTRIES.includes(localCompany.country));

const rules = computed(() => ({
  name: { required: validators.required },
  registry_code: {
    required: validators.required,
    validRegistryCode: validators.validRegistryCode,
  },
  address: {
    required: validators.required,
    validAddress: validators.validAddress,
  },
  phone_number: {
    required: validators.required,
    phoneNumber: validators.phoneNumber,
  },
  vat: { validVAT: validators.isValidVAT },
  country: { required: validators.required },
}));

const v$ = useVuelidate(rules, localCompany);

watch(
  () => props.company,
  (newCompany) => {
    Object.assign(localCompany, newCompany);
    v$.value.$reset();
  },
  { deep: true }
);

const handleCoreSignalCompanySelect = async (company: CoreSignalCompany) => {
  localCompany.name = company.name;

  // Fetch full company details for auto-fill
  if (company.id) {
    try {
      const details = await fetchCoreSignalCompanyDetails(company.id);

      if (details) {
        if (details.address) {
          localCompany.address = details.address;
        }
        if (details.registry_code) {
          localCompany.registry_code = details.registry_code;
        }
        if (details.vat) {
          localCompany.vat = details.vat;
        }
        if (details.country && details.country.length === 2) {
          localCompany.country = details.country.toUpperCase();
        }
      }
    } catch (error) {
      globalErrorHandler(error);
      // Fall back to preview data
      if (company.country && company.country.length === 2) {
        localCompany.country = company.country.toUpperCase();
      }
    }
  }
};

const handleSubmit = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) {
    return;
  }

  emit("submit", localCompany);
};
</script>

<style scoped lang="scss">
.corporate-company-form {
  max-width: 600px;
  margin: 0 auto;
}

.text-danger {
  color: #dc3545;
}
</style>
